// ─────────────────────────────────────────────────────────────────────────────
// KnowledgeBasePageComponent
// ─────────────────────────────────────────────────────────────────────────────
// Main page for the Knowledge Base feature.
// Responsibilities:
//   - Display page header with action buttons
//   - Manage two-level tab navigation (Documents | Products > Ext Refs | Alias)
//   - Persist active tab state via URL query params
//   - Host child tab content components
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

import { PageHeaderComponent } from 'src/app/ui/page-header/page-header.component';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { DocumentsTabComponent } from '../documents-tab/documents-tab.component';
import { ProductsTabComponent } from '../products-tab/products-tab.component';

@Component({
  selector: 'app-knowledge-base-page',
  standalone: true,
  imports: [CommonModule, TranslateModule, PageHeaderComponent, UiModule, SharedModule, DocumentsTabComponent, ProductsTabComponent],
  templateUrl: './knowledge-base-page.component.html',
  styleUrls: ['./knowledge-base-page.component.scss'],
})
export class KnowledgeBasePageComponent implements OnInit, OnDestroy {
  // ── Tab state ──────────────────────────────────────────────────────────

  /** Products is the landing tab — Documents is opened explicitly. */
  activeL1Tab: 'documents' | 'products' = 'products';

  // ── Cleanup ────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.activeL1Tab = params['tab'] === 'documents' ? 'documents' : 'products';
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Tab navigation ─────────────────────────────────────────────────────

  onL1TabChange(tab: 'documents' | 'products'): void {
    this.activeL1Tab = tab;
    const queryParams: Record<string, string | null> = { tab };
    if (tab === 'documents') {
      queryParams['subtab'] = null;
    }
    this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
  }
}
